// Every enabled vault's deposit-token logo, keyed by vault id.
//
// The plural counterpart to useDripVaultIcon, for callers that need all of them
// at once — a hook cannot be called per vault in a loop, so a page wanting N
// icons would otherwise hard-code one useDripVaultIcon call per vault id, and
// silently show no icon for the next LST added to the registry.
//
// Bundled icons resolve synchronously; the rest are fetched in one pass.

import { useEffect, useMemo, useState } from "react";
import { useDripVaults } from "../../hooks/useDripVaults";
import { fetchTokenIcon } from "../../hooks/utils/fetchTokenIcon";

/** vault id -> logo URL, undefined while loading or when nothing resolves. */
export type DripVaultIcons = Record<string, string | undefined>;

export const useDripVaultIcons = (): DripVaultIcons => {
  const vaults = useDripVaults();
  const [remoteByMint, setRemoteByMint] = useState<Record<string, string>>({});

  // Only vaults the registry does not bundle an icon for cost a request.
  const unbundledMints = useMemo(
    () => vaults.filter((vault) => !vault.icon).map((vault) => vault.depositMint),
    [vaults]
  );

  useEffect(() => {
    if (unbundledMints.length === 0) return;

    let cancelled = false;

    Promise.all(
      unbundledMints.map((mint) =>
        fetchTokenIcon(mint).then((icon) => [mint, icon] as const)
      )
    ).then((entries) => {
      if (cancelled) return;
      const resolved = entries.filter((entry): entry is [string, string] => !!entry[1]);
      if (resolved.length === 0) return;
      setRemoteByMint((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
    });

    return () => {
      cancelled = true;
    };
    // Mints are the identity here; the array is rebuilt only when the registry changes.
  }, [unbundledMints]);

  return useMemo(
    () =>
      Object.fromEntries(
        vaults.map((vault) => [vault.id, vault.icon ?? remoteByMint[vault.depositMint]])
      ),
    [vaults, remoteByMint]
  );
};
